// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { extractErrorMessage, type StrongOmit, useEvolvePost } from '@evolvemep/api-ts';
import { Button, Flex, Input, Loader, Text, Title, useMantineTheme } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import { useSearchParams } from 'react-router';

import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil } from 'helpers/isNotNil';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { type SignInFormValues, useSignInSteps } from '../auth/useSignInSteps';
import { AuthFormWrapper } from '../components/AuthFormWrapper';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { EmailStepFields } from '../components/EmailStepFields';
import type { LoginErrorCode } from '../components/LoginErrorAlert';
import { PasswordStepFields } from '../components/PasswordStepFields';
import {
  buildLoopbackCallbackUrl,
  type DesktopAuthRequest,
  desktopClients,
  parseDesktopAuthParams,
  extractDesktopAuthErrorCode,
} from './common';

type DesktopAuthMessageProps = {
  title: string;
  children: ReactNode;
};

const DesktopAuthMessage = ({ title, children }: DesktopAuthMessageProps) => (
  <Flex direction="column" gap="md" maw={400}>
    <Title fw={400}>{title}</Title>
    {children}
  </Flex>
);

type CancelButtonProps = {
  request: DesktopAuthRequest;
  disabled: boolean;
};

const CancelButton = ({ request: { redirectUri, state }, disabled }: CancelButtonProps) => (
  <Button
    variant="subtle"
    color="gray"
    ml="auto"
    disabled={disabled}
    onClick={() => window.location.replace(buildLoopbackCallbackUrl(redirectUri, { error: 'access_denied', state }))}
  >
    Cancel
  </Button>
);

type DesktopSignInFormProps = {
  request: DesktopAuthRequest;
  /** The `/desktop-auth` query string the desktop app sent, carried through the SSO round trip. */
  desktopRequest: string;
  /** The email of the website's signed-in user, offered as the default account. */
  sessionEmail: string | undefined;
  initialErrorCode: LoginErrorCode | string | undefined;
};

const DesktopSignInForm = ({ request, desktopRequest, sessionEmail, initialErrorCode }: DesktopSignInFormProps) => {
  const { clientId, redirectUri, state, codeChallenge } = request;
  const appName = isKeyOf(clientId, desktopClients) ? desktopClients[clientId] : undefined;
  const title = `Sign In to ${appName ?? 'EVOLVE'}`;
  const theme = useMantineTheme();
  const { apiCall: createDesktopCodeApiCall, loading: creating } = useEvolvePost('admin/authentication/desktop/code', {
    alertOnError: false,
  });
  const { form, showPasswordStep, submittingEmail, submitEmail, goToEmail, stepBoxProps } = useSignInSteps({
    initialEmail: sessionEmail,
    ssoState: { desktopRequest },
  });
  const [continuingAsSessionUser, setContinuingAsSessionUser] = useState(isNotNil(sessionEmail));
  const [errorCode, setErrorCode] = useState(initialErrorCode);
  const [returningToDesktop, setReturningToDesktop] = useState(false);
  const busy = submittingEmail || creating || returningToDesktop;

  const onSubmitEmail = async (values: SignInFormValues) => {
    setErrorCode(undefined);
    setErrorCode(await submitEmail(values));
  };

  const onSubmitPassword = async ({ email, password }: SignInFormValues) => {
    setErrorCode(undefined);
    try {
      const { code } = await createDesktopCodeApiCall({
        email,
        password,
        clientId,
        codeChallenge,
        codeChallengeMethod: 'S256',
        redirectUri,
      });
      setReturningToDesktop(true);
      window.location.replace(buildLoopbackCallbackUrl(redirectUri, { code, state }));
    } catch (e) {
      setErrorCode(extractErrorMessage(e));
    }
  };

  const onUseDifferentAccount = () => {
    form.setFieldValue('email', '');
    setContinuingAsSessionUser(false);
  };

  const onChangeLogin = () => {
    setErrorCode(undefined);
    setContinuingAsSessionUser(false);
    goToEmail();
  };

  if (returningToDesktop) {
    return (
      <DesktopAuthMessage title="Returning to the Desktop App">
        <Text>You can close this tab once {appName ?? 'the desktop app'} shows that you are signed in.</Text>
      </DesktopAuthMessage>
    );
  }

  if (showPasswordStep) {
    return (
      <AuthFormWrapper
        title={title}
        form={form}
        onSubmit={onSubmitPassword}
        errorCode={errorCode}
        onCloseError={() => setErrorCode(undefined)}
        boxProps={stepBoxProps}
      >
        <PasswordStepFields form={form} busy={busy} submitLabel="Sign in" onChangeLogin={onChangeLogin} />
        <CancelButton request={request} disabled={busy} />
      </AuthFormWrapper>
    );
  }

  return (
    <AuthFormWrapper
      title={title}
      form={form}
      onSubmit={onSubmitEmail}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
      boxProps={stepBoxProps}
    >
      {continuingAsSessionUser ? (
        <>
          <Input.Label mb={-10} size="md">
            Continue As
          </Input.Label>
          <Text c="gray.9">{form.values.email}</Text>
          <Button type="submit" loading={busy} disabled={!form.isValid()} fullWidth color={`${theme.primaryColor}.9`}>
            Continue
          </Button>
        </>
      ) : (
        <EmailStepFields form={form} busy={busy} />
      )}
      <Flex justify="space-between">
        {continuingAsSessionUser && (
          <Button variant="subtle" disabled={busy} onClick={onUseDifferentAccount}>
            Use a different account
          </Button>
        )}
        <CancelButton request={request} disabled={busy} />
      </Flex>
    </AuthFormWrapper>
  );
};

type ReturnErrorToDesktopProps = {
  errorCallbackUrl: string;
};

const ReturnErrorToDesktop = ({ errorCallbackUrl }: ReturnErrorToDesktopProps) => {
  useEffect(() => window.location.replace(errorCallbackUrl), [errorCallbackUrl]);

  return (
    <DesktopAuthMessage title="Returning to the Desktop App">
      <Text>The desktop app sent an invalid sign-in request. Try signing in from the desktop app again.</Text>
    </DesktopAuthMessage>
  );
};

type DesktopSignInProps = StrongOmit<DesktopSignInFormProps, 'sessionEmail'>;

const DesktopSignIn = (props: DesktopSignInProps) => {
  const { user, loading } = useMaybeUser();
  if (loading) return <Loader />;
  return <DesktopSignInForm {...props} sessionEmail={user?.userEmail} />;
};

/**
 * Lets a desktop app (e.g. the EVOLVE Revit plugin) sign in through the website.
 * The app opens this page in the system browser; the user signs in with a password or SSO
 * (a fresh sign-in that leaves the website's own session untouched), and the browser is then
 * redirected to the app's loopback listener with a single-use code.
 */
export const DesktopAuthPage = () => {
  const [searchParams] = useSearchParams();
  const request = useMemo(() => parseDesktopAuthParams(searchParams), [searchParams]);
  const { desktopRequest, errorCode } = useMemo(() => extractDesktopAuthErrorCode(searchParams), [searchParams]);

  return (
    <AuthPageLayout>
      {request.valid ? (
        <DesktopSignIn request={request} desktopRequest={desktopRequest} initialErrorCode={errorCode} />
      ) : isNotNil(request.errorCallbackUrl) ? (
        <ReturnErrorToDesktop errorCallbackUrl={request.errorCallbackUrl} />
      ) : (
        <DesktopAuthMessage title="Unable to Sign In">
          <Text>The sign-in link from the desktop app is invalid. Try signing in from the desktop app again.</Text>
          <Text fz="sm" c="dimmed">
            {request.reason}
          </Text>
        </DesktopAuthMessage>
      )}
    </AuthPageLayout>
  );
};
